import { ValueOf } from '..';
import {
  RuleCategory,
  SiteClassification,
  TimeRestrictions,
  WebDomainRules,
} from '../api';
import { SafeNetworksStore } from './safeNetworks';
import { ScreenTime } from './screenTime';
import { ScreenTimeEvent } from '../api/events';
import { ConsoleEvent } from '../../logging/console';

export type StoreMethods<T extends ValueOf<Stores>> = {
  set: (values: T extends object ? Partial<T> : T) => Promise<void>;
  get: () => T | undefined;
  clear: () => Promise<void>;
};

export enum StoresKeys {
  CREDENTIALS = 'qustodio:credentials',
  ACCOUNT = 'qustodio:account',
  DEVICE_AND_USER = 'qustodio:device_user',
  RULES = 'qustodio:rules',
  TIMES = 'qustodio:times',
  CLASSIFICATIONS = 'qustodio:classifications_lru',
  API = 'qustodio:api',
  DEVICE = 'qustodio:device',
  SAFE_NETWORKS = 'qustodio:safe_networks',
  PLATFORM_INFO = 'qustodio:platform_info',
  DELEGATION = 'qustodio:delegation',
  SCREEN_TIME = 'qustodio:screen_time',
  REPORTING = 'qustodio:reporting',
  REPORTED_DEVICE_INFO = 'qustodio:reported_device_info',
  TIME_NOTIFICATIONS = 'qustodio:time_notifications',
  LOGS = 'qustodio:logs',
}

export interface Stores {
  [StoresKeys.CREDENTIALS]: CredentialsStore;
  [StoresKeys.ACCOUNT]: AccountStore;
  [StoresKeys.DEVICE_AND_USER]: DeviceAndUserStore;
  [StoresKeys.RULES]: RulesStore;
  [StoresKeys.TIMES]: TimeRestrictions;
  [StoresKeys.CLASSIFICATIONS]: ClassificationStore;
  [StoresKeys.API]: ApiStore;
  [StoresKeys.DEVICE]: DeviceStore;
  [StoresKeys.SAFE_NETWORKS]: SafeNetworksStore;
  [StoresKeys.PLATFORM_INFO]: PlatformInfoStore;
  [StoresKeys.DELEGATION]: DelegationStore;
  [StoresKeys.SCREEN_TIME]: ScreenTimeStore;
  [StoresKeys.REPORTING]: ReportingStore;
  [StoresKeys.REPORTED_DEVICE_INFO]: ReportedDeviceInfoStore;
  [StoresKeys.TIME_NOTIFICATIONS]: TimeNotificationsStore;
  [StoresKeys.LOGS]: LogsStore;
}

export type AccountStore = {
  accountId: number;
  accountUid: string;
  masterAccountId: number | null;
  masterAccountUid: string | null;
  locale: string;
  timezone: string;
};

export type CredentialsStore = {
  accessToken: string;
  refreshToken: string;
};

export type FCMInfo = {
  pushToken?: string;
  lastSentToken?: string;
};

export type DeviceAndUserStore = {
  device: string;
  user: string;
  profileId: number;
  profileName: string;
  consumedQuotaSeconds: number;
  fcm?: FCMInfo;
};

export type RulesStore = {
  timestamp: string;
  timeRestrictions: TimeRestrictions;
  categories: RuleCategory[];
  allowUnknownSites: boolean;
  isCategoryList: boolean;
  isSafeSearch: boolean;
  domainRules: WebDomainRules;
  safeNetworksRules: SafeNetworksRules;
};

export type SafeNetworksRules = {
  delegateProtectionInSafeNetwork: boolean;
};

export type ClassificationStore = {
  classifications: {
    key: string;
    value: SiteClassification;
  }[];
};

export type LogsStore = {
  logs: {
    key: string;
    value: ConsoleEvent;
  }[];
};

export type ApiStore = {
  lastUpdateLastSeen: string;
};

export type DeviceStore = {
  /** Is delegated by Safe Network */
  isProtectionDelegated: boolean;
  lastNetworkConnectionType: ConnectionType;
};

export type PlatformInfoStore = {
  arch: string;
  os: string;
};

export type DelegationStore = {
  isLoggedIn: boolean;
  isDelegationStarted: boolean;
};

export type ScreenTimeStore = {
  screenTime: ScreenTime;
};

export type ReportingStore = {
  timestamp: number; // last timestamp reporting with success
  events: {
    screenTime: ScreenTimeEvent[];
  };
};

export type ReportedDeviceInfoStore = {
  extensionVersion: string;
  osVersion: string;
  isDelegationStarted: boolean;
};

export type TimeNotificationsStore = {
  isNotified5MinutesForDailyQuota: boolean;
  isNotified15MinutesForDailyQuota: boolean;
};
